/**
 * ADA (Accessibility) global fixes for third-party integrations.
 */
import { createFocusTrap, type FocusTrap } from 'focus-trap'

function fixPandectesCloseButtonLabel() {
  if (!window.PandectesSettings) return

  const CLOSE_BUTTON_SELECTOR = '.cc-close'
  const ARIA_LABEL = 'Close'

  let executed = false

  const observer = new MutationObserver(() => {
    if (executed) return

    const closeButton = document.querySelector<HTMLButtonElement>(CLOSE_BUTTON_SELECTOR)

    if (closeButton) {
      closeButton.setAttribute('aria-label', ARIA_LABEL)
      executed = true
      observer.disconnect()
    }
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true
  })
}

/**
 * When the Pandectes cookie banner (`.cc-window-wrapper`) is visible, trap focus inside it
 * and move initial focus to the first tabbable control. On hide, return focus to the active
 * element from before the modal was shown (`returnFocusOnDeactivate`).
 */
function trapPandectesCookieBannerFocus() {
  if (!window.PandectesSettings) return

  const WRAPPER_SELECTOR = '.cc-window-wrapper'

  let focusTrap: FocusTrap | null = null
  let trappedWrapper: HTMLElement | null = null
  let wasVisible = false

  const deactivateTrap = () => {
    if (!focusTrap) return

    focusTrap.deactivate()
    focusTrap = null
    trappedWrapper = null
  }

  const isWrapperDisplayed = (el: HTMLElement) => getComputedStyle(el).display !== 'none'

  const activateTrapOnWrapper = (wrapper: HTMLElement) => {
    focusTrap = createFocusTrap(wrapper, {
      returnFocusOnDeactivate: true,
      escapeDeactivates: false,
      clickOutsideDeactivates: false,
      allowOutsideClick: false,
      fallbackFocus: () => {
        wrapper.tabIndex = -1

        return wrapper
      }
    })
    trappedWrapper = wrapper
    focusTrap.activate()
  }

  const syncFocusTrap = () => {
    const cookieBannerWrapper = document.querySelector<HTMLElement>(WRAPPER_SELECTOR)
    const isVisible = Boolean(cookieBannerWrapper && isWrapperDisplayed(cookieBannerWrapper))

    if (!isVisible && !wasVisible) return
    if (isVisible && wasVisible && focusTrap && trappedWrapper === cookieBannerWrapper) return

    if (!cookieBannerWrapper) {
      if (wasVisible) deactivateTrap()
      wasVisible = false

      return
    }

    // Pandectes may swap `.cc-window-wrapper` without toggling visibility; re-bind trap to the live node.
    if (isVisible && wasVisible && trappedWrapper !== cookieBannerWrapper) {
      deactivateTrap()

      try {
        activateTrapOnWrapper(cookieBannerWrapper)
      } catch {
        focusTrap = null
        trappedWrapper = null
        wasVisible = false
      }

      return
    }

    if (isVisible && !wasVisible) {
      deactivateTrap()

      try {
        activateTrapOnWrapper(cookieBannerWrapper)
        wasVisible = true
      } catch {
        focusTrap = null
        trappedWrapper = null
        wasVisible = false
      }

      return
    }

    if (!isVisible && wasVisible) {
      deactivateTrap()
      wasVisible = false
    }
  }

  let syncFrame = 0

  const scheduleSync = () => {
    if (syncFrame !== 0) return

    syncFrame = requestAnimationFrame(() => {
      syncFrame = 0
      syncFocusTrap()
    })
  }

  new MutationObserver(scheduleSync).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden']
  })

  syncFocusTrap()
}

const PRODUCT_CARD_TITLE_SELECTOR = '.product-card .product-title'
const PRODUCT_CARD_MEDIA_LINK_SELECTOR = 'a.product-card__media'

const bindProductCardTitleNavigation = () => {
  const onDocumentClick = (event: MouseEvent) => {
    const target = event.target

    if (!(target instanceof Element)) return

    const titleEl = target.closest<HTMLElement>(PRODUCT_CARD_TITLE_SELECTOR)

    if (!titleEl) return

    const productCard = titleEl.closest<HTMLElement>('.product-card')
    const mediaLink = productCard?.querySelector<HTMLAnchorElement>(PRODUCT_CARD_MEDIA_LINK_SELECTOR)

    if (!mediaLink) return

    event.preventDefault()
    mediaLink.click()
  }

  document.addEventListener('click', onDocumentClick, { capture: true })
}

const enableKeyboardFocusOutlines = () => {
  const root = document.documentElement

  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      event.key === 'Tab' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      root.classList.add('user-is-tabbing')
    }
  }

  const handleMouseDown = () => {
    root.classList.remove('user-is-tabbing')
  }

  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('mousedown', handleMouseDown)
}

enableKeyboardFocusOutlines()
fixPandectesCloseButtonLabel()
trapPandectesCookieBannerFocus()
bindProductCardTitleNavigation()
